import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../../client/cn";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  showCloseButton?: boolean;
  closeLabel?: string;
  closeButtonClassName?: string;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  className = "",
  overlayClassName = "",
  titleClassName = "",
  descriptionClassName = "",
  showCloseButton = false,
  closeLabel = "Close dialog",
  closeButtonClassName = "",
}: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn("fixed inset-0 z-50 bg-black/30", overlayClassName)}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl focus:outline-hidden dark:bg-boxdark",
            className,
          )}
        >
          <div className="relative">
            {showCloseButton && (
              <DialogPrimitive.Close
                className={cn(
                  "absolute right-0 top-0 inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30 dark:text-neutral-300 dark:hover:bg-neutral-800 dark:hover:text-white",
                  closeButtonClassName,
                )}
              >
                <X size={16} aria-hidden="true" />
                <span className="sr-only">{closeLabel}</span>
              </DialogPrimitive.Close>
            )}
            <DialogPrimitive.Title
              className={cn(
                "text-lg font-medium text-neutral-900 dark:text-white",
                showCloseButton && "pr-10",
                titleClassName,
              )}
            >
              {title}
            </DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description
                className={cn(
                  "mt-2 text-sm text-neutral-500 dark:text-neutral-400",
                  showCloseButton && "pr-10",
                  descriptionClassName,
                )}
              >
                {description}
              </DialogPrimitive.Description>
            )}
          </div>
          <div className="mt-4">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
